import {
  CLASSIFICATION_SORT_ORDER,
  RECOMMENDATION_SORT_ORDER,
} from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { isPopulatedObject } from '@/lib/utils'
import { isDiscontinued } from '@/lib/app-categories/discontinued'
import type { App, AppCategoryRecommendation, Category } from '@/payload-types'

export type CategoryGroupMapping = {
  groupId: number
  groupName: string
  order: number
}

export type CategoryGroupBucket = {
  id: string
  title: string
  scope: 'categoryGroup' | 'category'
  category: Category
  order: number
  appCategoryRecommendations: AppCategoryRecommendation[]
}

type RecommendationPriority = 'classification' | 'recommendation' | 'bestFor' | 'order'

const BEST_CATEGORY_PRIORITIES: RecommendationPriority[] = [
  'classification',
  'recommendation',
  'order',
]

const STACK_PRIORITIES: RecommendationPriority[] = [
  'classification',
  'recommendation',
  'bestFor',
  'order',
]

function hasBestFor(recommendation: AppCategoryRecommendation): boolean {
  return Boolean(recommendation.bestFor?.trim())
}

function compareRecommendationPriority(
  a: AppCategoryRecommendation,
  b: AppCategoryRecommendation,
  priority: RecommendationPriority,
): number {
  switch (priority) {
    case 'classification':
      return (
        (CLASSIFICATION_SORT_ORDER[a.classification] ?? 999) -
        (CLASSIFICATION_SORT_ORDER[b.classification] ?? 999)
      )
    case 'recommendation':
      return (
        (RECOMMENDATION_SORT_ORDER[a.recommendation] ?? 999) -
        (RECOMMENDATION_SORT_ORDER[b.recommendation] ?? 999)
      )
    case 'bestFor': {
      const aHasBestFor = hasBestFor(a)
      const bHasBestFor = hasBestFor(b)
      if (aHasBestFor === bHasBestFor) return 0
      return aHasBestFor ? -1 : 1
    }
    case 'order':
      return (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
  }
}

function isBetterRecommendation(
  a: AppCategoryRecommendation,
  b: AppCategoryRecommendation,
  priorities: RecommendationPriority[],
): boolean {
  for (const priority of priorities) {
    const diff = compareRecommendationPriority(a, b, priority)
    if (diff !== 0) return diff < 0
  }

  return false
}

function getBestRecommendationForApp(
  app: App,
  priorities: RecommendationPriority[],
): AppCategoryRecommendation | null {
  const recommendations = app.categoryRecommendations?.docs ?? []
  let bestRecommendation: AppCategoryRecommendation | null = null

  for (const recommendation of recommendations) {
    if (!isPopulatedObject<AppCategoryRecommendation>(recommendation)) continue
    if (isDiscontinued(recommendation)) continue
    if (!isPopulatedObject<Category>(recommendation.category)) continue

    const normalizedRecommendation: AppCategoryRecommendation = {
      ...recommendation,
      app,
      category: recommendation.category,
    }

    if (!bestRecommendation) {
      bestRecommendation = normalizedRecommendation
      continue
    }

    if (!isBetterRecommendation(normalizedRecommendation, bestRecommendation, priorities)) continue

    bestRecommendation = normalizedRecommendation
  }

  return bestRecommendation
}

function buildCategoryGroupBuckets(
  apps: App[],
  categoryGroupLookup: Record<number, CategoryGroupMapping>,
  priorities: RecommendationPriority[],
): CategoryGroupBucket[] {
  const buckets = new Map<string, CategoryGroupBucket>()
  const appIds = new Set<number>()

  for (const app of apps) {
    if (!app.id || appIds.has(app.id)) continue

    const bestRecommendation = getBestRecommendationForApp(app, priorities)
    if (!bestRecommendation || !isPopulatedObject<Category>(bestRecommendation.category)) continue

    const cat = bestRecommendation.category
    const groupMapping = categoryGroupLookup[cat.id]

    appIds.add(app.id)

    if (groupMapping) {
      const bucketKey = `category-group-${groupMapping.groupId}`
      const existingBucket = buckets.get(bucketKey)

      if (existingBucket) {
        existingBucket.appCategoryRecommendations.push(bestRecommendation)
      } else {
        buckets.set(bucketKey, {
          id: bucketKey,
          title: groupMapping.groupName,
          scope: 'categoryGroup',
          category: cat,
          order: groupMapping.order,
          appCategoryRecommendations: [bestRecommendation],
        })
      }
    } else {
      const bucketKey = `category-${cat.id}`
      const existingBucket = buckets.get(bucketKey)

      if (existingBucket) {
        existingBucket.appCategoryRecommendations.push(bestRecommendation)
      } else {
        buckets.set(bucketKey, {
          id: bucketKey,
          title: cat.name,
          scope: 'category',
          category: cat,
          order: Number.MAX_SAFE_INTEGER,
          appCategoryRecommendations: [bestRecommendation],
        })
      }
    }
  }

  return [...buckets.values()].sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order
    return a.title.localeCompare(b.title)
  })
}

export function buildBestPrimaryCategoryBuckets(apps: App[]): CategoryGroupBucket[] {
  const buckets = new Map<string, CategoryGroupBucket>()
  const appIds = new Set<number>()

  for (const app of apps) {
    if (!app.id || appIds.has(app.id)) continue

    const bestRecommendation = getBestRecommendationForApp(app, BEST_CATEGORY_PRIORITIES)
    if (!bestRecommendation || !isPopulatedObject<Category>(bestRecommendation.category)) continue

    const cat = bestRecommendation.category
    const bucketKey = `category-${cat.id}`
    const existingBucket = buckets.get(bucketKey)

    appIds.add(app.id)

    if (existingBucket) {
      existingBucket.appCategoryRecommendations.push(bestRecommendation)
    } else {
      buckets.set(bucketKey, {
        id: bucketKey,
        title: cat.name,
        scope: 'category',
        category: cat,
        order: buckets.size,
        appCategoryRecommendations: [bestRecommendation],
      })
    }
  }

  return [...buckets.values()].sort((a, b) => a.order - b.order)
}

export function buildStackCategoryGroupBuckets(
  apps: App[],
  categoryGroupLookup: Record<number, CategoryGroupMapping>,
): CategoryGroupBucket[] {
  // Stack pages group apps by their primary category first, then by recommendation strength.
  return buildCategoryGroupBuckets(apps, categoryGroupLookup, STACK_PRIORITIES)
}
